import * as React from 'react';
import { alpha } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import Alert from '@mui/material/Alert';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import Slide from '@mui/material/Slide';
import { useReactiveVar } from '@apollo/client';
import { TransitionProps } from '@mui/material/transitions';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { FormikErrors, useFormik } from 'formik';
import * as yup from 'yup';
import moment, { Moment } from 'moment-timezone';
import * as Device from 'react-device-detect';

import {
  BookingOccasionEnum,
  useBookByConsumerMutation,
  GetSpotDetailedQuery,
  BookByConsumerMutation,
} from '@graphql';
import {
  shouldDisableBookingDate,
  shouldDisableBookingTime,
  initBookingDateTime,
  getDateSchedules,
} from '@utils';
import { loginStatusVar } from '@context';
import { useSession, useToastError } from '@hooks';
import RegisterMobilePhone from './RegisterMobilePhone';

interface BookingButtonProps {
  spot: GetSpotDetailedQuery['spot'];
}

export default function BookingButton(props: Readonly<BookingButtonProps>) {
  const { spot } = props;
  const [open, setOpen] = React.useState(false);
  const [preview, setPreview] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const loginStatus = useReactiveVar(loginStatusVar);
  const theme = useTheme();
  const { user } = useSession();
  const { t } = useTranslation('cta');
  const toastError = useToastError();
  const [dateSchedules, setDateSchedules] = React.useState<string | null>(null);
  const [isMobilePhoneOpen, setIsMobilePhoneOpen] = React.useState(false);

  const [bookSpot, { data: newBooking }] = useBookByConsumerMutation();

  const getInitalValues = () => {
    const defaultValues = {
      startTime: moment()
        .tz(spot.address.timezone ?? 'Europe/Paris')
        .add(1, 'hour')
        .set({ minute: 0, second: 0, millisecond: 0 }),
      participants: '2',
      occasion: '',
      message: '',
    };

    const schedules = { opening: spot.opening, closing: spot.closing };
    const initialStartTime = initBookingDateTime(schedules, moment());

    if (initialStartTime.isValid()) {
      defaultValues.startTime = initialStartTime;
    }

    return defaultValues;
  };

  const formik = useFormik({
    initialValues: getInitalValues(),
    validationSchema: yup.object({
      startTime: yup
        .date()
        .min(moment(), t('bookingStartTime.validation.min', { ns: 'field' }))
        .required(),
      participants: yup.number().min(1).required(),
      occasion: yup.string().oneOf(['', ...Object.values(BookingOccasionEnum)]),
      message: yup.string(),
    }),
    onSubmit: async (values) => {
      if (!values.startTime) {
        return;
      }
      const validValues = {
        spotId: spot.id,
        startTime: values.startTime.toISOString(),
        participants: parseInt(values.participants, 10),
        occasion: values.occasion.length ? (values.occasion as BookingOccasionEnum) : null,
        date: values.startTime.format('YYYY-MM-DD'),
        time: values.startTime.format('HH:mm:ss'),
        message: values.message.length ? values.message : null,
      };

      bookSpot({
        variables: { input: validValues },
        onCompleted() {
          setSuccess(true);
        },
        onError: toastError,
      });
    },
  });

  React.useEffect(() => {
    const dateSchedules = getDateSchedules(
      spot.opening,
      spot.closing,
      moment(formik.values.startTime).startOf('day')
    );

    setDateSchedules(dateSchedules);

    const isClosed = spot
      ? shouldDisableBookingDate(moment(formik.values.startTime), spot.opening, spot.closing)
      : true;

    formik.setErrors({
      ...formik.errors,
      ...(isClosed && {
        startTime: t('error.closed', {
          ns: 'booking',
          name: spot.name,
        }) as unknown as FormikErrors<Moment>,
      }),
    });
  }, [formik.values.startTime]);

  const handleValidate = () => {
    if (!preview) {
      setPreview(true);
    } else {
      formik.handleSubmit();
    }
  };

  const handleCancel = () => {
    if (preview) {
      setPreview(false);
    } else {
      handleClose();
    }
  };

  const handleClickOpen = () => {
    if (!user) {
      loginStatusVar({
        ...loginStatus,
        shouldLogFirst: true,
      });
    } else if (!user.mobilePhone) {
      setIsMobilePhoneOpen(true);
    } else {
      setOpen(true);
    }
  };

  const handleCloseRegisterMobilePhone = (success = false) => {
    setIsMobilePhoneOpen(false);
    success && setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSuccess(false);
    setPreview(false);
    formik.resetForm({
      values: getInitalValues(),
    });
  };

  if (!spot) {
    return null;
  }

  return (
    <React.Fragment>
      <Button variant="contained" color="primary" fullWidth onClick={handleClickOpen}>
        {t('book')}
      </Button>
      <RegisterMobilePhone open={isMobilePhoneOpen} handleOnClose={handleCloseRegisterMobilePhone} />
      <Dialog fullScreen open={open} onClose={handleClose} slots={{ transition: Transition }}>
        <AppBar
          position="fixed"
          sx={{
            backgroundColor: theme.palette.background.default,
            // handle safe area insets for iOS devices
            ...(Device.isMobile &&
              !Device.isBrowser && {
                pt: 'env(safe-area-inset-top)',
                boxSizing: 'border-box',
              }),
          }}
        >
          <Toolbar width="100%" sx={{ justifyContent: 'space-between' }}>
            <Typography sx={{ flex: 1 }} variant="h6" component="div">
              {t('word.Book', { ns: 'common' })}
            </Typography>
            <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Stack justifyContent="center" alignItems="center" width="100%" height="100%" mt={2}>
          {success && newBooking && (
            <BookingSuccess booking={newBooking.bookByConsumer} handleClose={handleClose} />
          )}
          {!success && (
            <Stack
              px={5}
              spacing={3}
              width="100%"
              maxWidth={Device.isMobileOnly ? '100%' : 500}
              component="form"
              justifyContent="center"
              alignItems="center"
              onSubmit={formik.handleSubmit}
              divider={
                preview ? (
                  <Divider sx={{ backgroundColor: theme.palette.grey[100], width: 'inherit' }} />
                ) : undefined
              }
            >
              <Typography variant="h6" color="textPrimary">
                {spot.name}
              </Typography>
              {!preview && user && (
                <Stack spacing={1} width="100%">
                  <Alert severity="info" icon={false} sx={{ width: '100%' }}>
                    <Typography variant="body2" fontWeight={500}>
                      {t('alert.Schedule for the selected day', {
                        ns: 'booking',
                        dateSchedules: dateSchedules,
                      })}
                    </Typography>
                  </Alert>
                </Stack>
              )}
              {preview ? (
                <Stack justifyContent="space-between" width="100%" spacing={3} divider={<Divider />}>
                  <Stack direction="row" justifyContent="space-between" width="100%">
                    <Typography variant="body2" fontWeight={600} color="textSecondary">
                      {t('word.Date', { ns: 'common' })}
                    </Typography>
                    <Typography variant="body2">{formik.values.startTime.format('LL')}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" width="100%">
                    <Typography variant="body2" fontWeight={600} color="textSecondary">
                      {t('word.Hour', { ns: 'common' })}
                    </Typography>
                    <Typography variant="body2">{formik.values.startTime.format('HH:mm')}</Typography>
                  </Stack>
                </Stack>
              ) : (
                <DateTimePicker
                  label={t('bookingStartTime.label', { ns: 'field' })}
                  disablePast
                  ampm={false}
                  value={formik.values.startTime}
                  onChange={(value) => {
                    formik.setFieldValue('startTime', value);
                    formik.setFieldTouched('startTime', true);
                  }}
                  shouldDisableDate={(date) => {
                    return shouldDisableBookingDate(date, spot.opening, spot.closing);
                  }}
                  shouldDisableTime={(date) => {
                    return shouldDisableBookingTime(date, spot.opening, spot.closing);
                  }}
                  slotProps={{
                    textField: {
                      required: true,
                      error: formik.touched.startTime && Boolean(formik.errors.startTime),
                      helperText: formik.errors.startTime
                        ? (formik.errors.startTime as unknown as string)
                        : null,
                    },
                  }}
                  sx={{ width: '100%' }}
                />
              )}
              {preview ? (
                <Stack direction="row" justifyContent="space-between" width="100%">
                  <Typography variant="body2" fontWeight={600} color="textSecondary">
                    {t('participants.label', { ns: 'field' })}
                  </Typography>
                  <Typography variant="body2">{formik.values.participants}</Typography>
                </Stack>
              ) : (
                <TextField
                  {...formik.getFieldProps('participants')}
                  required
                  fullWidth
                  variant="outlined"
                  slotProps={{ input: { min: 1 } }}
                  type="number"
                  id="book-participants"
                  name="participants"
                  label={t('participants.label', { ns: 'field' })}
                  onChange={(e) => {
                    const value = Math.max(1, parseInt(e.target.value, 10));
                    formik.setFieldValue('participants', isNaN(value) ? '' : value);
                  }}
                  error={formik.touched.participants && Boolean(formik.errors.participants)}
                  disabled={Boolean(formik.errors.startTime)}
                  helperText={
                    formik.touched.participants && formik.errors.participants
                      ? String(formik.errors.participants)
                      : null
                  }
                />
              )}
              {preview ? (
                <Stack direction="row" justifyContent="space-between" width="100%">
                  <Typography variant="body2" fontWeight={600} color="textSecondary">
                    {t('occasion select.label', { ns: 'field' })}
                  </Typography>
                  <Typography variant="body2">
                    {formik.values.occasion.length
                      ? t(`occasion.${formik.values.occasion}`, { ns: 'booking' })
                      : '-'}
                  </Typography>
                </Stack>
              ) : (
                <FormControl fullWidth>
                  <InputLabel id="occasion-select-label">
                    {t('occasion select.label', { ns: 'field' })}
                  </InputLabel>
                  <Select
                    {...formik.getFieldProps('occasion')}
                    labelId="occasion-select-label"
                    id="occasion-select"
                    name="occasion"
                    label={t('occasion select.label', { ns: 'field' })}
                    value={formik.values.occasion}
                    disabled={Boolean(formik.errors.startTime)}
                  >
                    <MenuItem value="">
                      <em>{t('occasion select.placeholder', { ns: 'field' })}</em>
                    </MenuItem>
                    {Object.entries(BookingOccasionEnum).map(([key, value]) => (
                      <MenuItem key={key} value={value}>
                        {t(`suitable.${key.charAt(0).toLowerCase() + key.slice(1)}`, {
                          ns: 'spot',
                        })}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {preview && user && (
                <Stack
                  spacing={1}
                  width="100%"
                  sx={{
                    borderRadius: 2,
                    p: 2,
                    border: `1px solid ${theme.palette.grey[800]}`,
                    backgroundColor: alpha(theme.palette.background.paper, 0.8),
                  }}
                >
                  <Typography variant="body2" fontWeight={500}>
                    {t('bookPreview.My contact information', {
                      ns: 'booking',
                    })}
                  </Typography>
                  <Divider />
                  <Stack direction="row" width="100%" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" fontWeight={500}>
                      {t('bookPreview.Firstname and lastname', {
                        ns: 'booking',
                      })}
                    </Typography>
                    <Typography variant="caption" textAlign="right" fontWeight={600}>
                      {user.firstname + ' ' + user.lastname + '.'}
                    </Typography>
                  </Stack>
                  <Stack direction="row" width="100%" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" fontWeight={500}>
                      {t('alert.Phone contact', {
                        ns: 'booking',
                      })}
                    </Typography>
                    <Typography variant="caption" textAlign="right" fontWeight={600}>
                      {user.mobilePhone}
                    </Typography>
                  </Stack>
                </Stack>
              )}
              {preview && (
                <TextField
                  {...formik.getFieldProps('message')}
                  fullWidth
                  variant="outlined"
                  slotProps={{ input: { min: 1 } }}
                  id="book-message"
                  name="message"
                  multiline
                  minRows={5}
                  maxRows={10}
                  size="small"
                  label={t('message.label', { ns: 'field' }) + '...'}
                  placeholder={t('message.placeholder', { ns: 'field' })}
                  error={formik.touched.message && Boolean(formik.errors.message)}
                  disabled={Boolean(formik.errors.startTime)}
                  helperText={
                    formik.touched.message && formik.errors.message ? String(formik.errors.message) : null
                  }
                />
              )}
              <Stack direction="row" spacing={2} justifyContent="space-between" width="100%">
                <Button variant="contained" color="uncolored" onClick={handleCancel}>
                  {t('cancel', { ns: 'cta' })}
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleValidate}
                  color={
                    !(formik.dirty || formik.isValid || Boolean(formik.errors.startTime))
                      ? 'disabled'
                      : 'success'
                  }
                  disabled={!(formik.dirty || formik.isValid) || Boolean(formik.errors.startTime)}
                >
                  {t(`${!preview ? 'see' : 'send'} my request`, { ns: 'cta' })}
                </Button>
              </Stack>
            </Stack>
          )}
        </Stack>
      </Dialog>
    </React.Fragment>
  );
}

interface BookingSuccessProps {
  booking: BookByConsumerMutation['bookByConsumer'];
  handleClose: () => void;
}

function BookingSuccess(props: Readonly<BookingSuccessProps>) {
  const { booking, handleClose } = props;
  const message = booking?.updates?.[0].message ?? null;
  const theme = useTheme();
  const { t } = useTranslation('cta');

  return (
    <Stack spacing={3}>
      <Alert severity="success">
        {t('alert.Your reservation has been successfully registered.', { ns: 'booking' })}
      </Alert>
      <Stack spacing={1} divider={<Divider sx={{ backgroundColor: theme.palette.grey[800] }} />}>
        <Stack direction="row" justifyContent="space-between">
          <Typography>{t('word.Place', { ns: 'common' })}</Typography>
          <Typography>{booking.spot.name}</Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between">
          <Typography>{t('word.Date', { ns: 'common' })}</Typography>
          <Typography>{moment(booking.startTime).format('dddd D MMMM YYYY')}</Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between">
          <Typography>{t('word.Hour', { ns: 'common' })}</Typography>
          <Typography>{moment(booking.startTime).tz('Europe/Paris').format('HH:mm')}</Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between">
          <Typography>{t('word.Participants', { ns: 'common' })}</Typography>
          <Typography>{booking.participants}</Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between">
          <Typography>{t('word.Occasion', { ns: 'common' })}</Typography>
          <Typography>
            {booking.occasion ? t(`occasion.${booking.occasion}`, { ns: 'booking' }) : '-'}
          </Typography>
        </Stack>
        {message && (
          <Stack
            spacing={1}
            p={2}
            sx={{ backgroundColor: theme.palette.background.default, borderRadius: 2 }}
          >
            <Typography>{t('word.Message', { ns: 'common' })}</Typography>
            <Divider />
            <Typography variant="body2">{message ?? '-'}</Typography>
          </Stack>
        )}
      </Stack>
      <Alert severity="info" icon={false}>
        <Stack spacing={1}>
          <Typography variant="body2">
            {t('alert.Your reservation status is', { ns: 'booking' }) +
              t('status.PENDING', { ns: 'booking' })}
          </Typography>
          <Typography variant="body2">
            {t('alert.It will soon be treated by', { ns: 'booking', spot: booking.spot.name })}
          </Typography>
        </Stack>
      </Alert>
      <Button variant="contained" color="uncolored" onClick={handleClose}>
        {t('close')}
      </Button>
    </Stack>
  );
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<unknown>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});
